// A page can replace the header's section title (e.g. the Ask conversation's generated title). The override lives in
// the browser's memory only and is cleared when the page that set it goes away.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface PageTitleApi {
  title: string | null;
  setTitle: (title: string | null) => void;
}

const PageTitleContext = createContext<PageTitleApi>({ title: null, setTitle: () => undefined });

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  const value = useMemo(() => ({ title, setTitle }), [title]);
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

export function usePageTitleOverride(): string | null {
  return useContext(PageTitleContext).title;
}

/** Sets the header title while the calling page is mounted (null keeps the default). */
export function useSetPageTitle(title: string | null): void {
  const { setTitle } = useContext(PageTitleContext);
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
}
